import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { 
  Store, Plus, Trash2, RefreshCw, 
  CheckCircle, AlertCircle, Settings, Clock, Activity
} from 'lucide-react';

interface Shop {
  id: string;
  shopName: string;
  platformType: string;
  isActive: boolean;
  lastSyncAt: string | null;
  syncSettings: {
    inventoryEnabled: boolean;
    ordersEnabled: boolean;
    productsEnabled: boolean;
    syncFrequency: string;
  } | null;
  platform: { name: string; color: string; authType: string };
  orderCount: number;
  mappingCount: number;
}

interface Platform {
  name: string;
  color: string;
  authType: string;
}

export default function ShopsPage() {
  const [shops, setShops] = useState<Shop[]>([]);
  const [platforms, setPlatforms] = useState<Record<string, Platform>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingShop, setViewingShop] = useState<string | null>(null);

  useEffect(() => {
    fetchShops();
    fetchPlatforms();
  }, []);

  async function fetchShops() {
    setLoading(true);
    try {
      const response = await apiFetch('/api/shops');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setShops(Array.isArray(data.shops) ? data.shops : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  async function fetchPlatforms() {
    try {
      const response = await apiFetch('/api/shops/platforms');
      const data = await response.json();
      if (response.ok) setPlatforms(data.platforms);
    } catch (err) {
      console.error(err);
    }
  }

  async function testConnection(shopId: string) {
    try {
      const response = await apiFetch(`/api/shops/${shopId}/test`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setSuccess('接続テスト成功');
    } catch (err) {
      setError(err instanceof Error ? err.message : '接続テスト失敗');
    }
  }

  async function syncShop(shopId: string, syncType: string) {
    try {
      const response = await apiFetch(`/api/shops/${shopId}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ syncType }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setSuccess(data.message);
      fetchShops();
    } catch (err) {
      setError(err instanceof Error ? err.message : '同期実行失敗');
    }
  }

  async function deleteShop(shopId: string) {
    if (!confirm('この店舗を無効化しますか？')) return;

    try {
      const response = await apiFetch(`/api/shops/${shopId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }
      setSuccess('店舗を無効化しました');
      fetchShops();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    }
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Store size={28} />
            店舗連携
          </h1>
          <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
            ECモール・管理システムとのAPI連携を管理
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          店舗追加
        </button>
      </div>

      {/* メッセージ */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">×</button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle size={18} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto">×</button>
        </div>
      )}

      {/* 店舗一覧 */}
      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : shops.length === 0 ? (
        <div className="card p-8 text-center">
          <Store size={48} className="mx-auto mb-4 opacity-50" />
          <p style={{ color: 'var(--text-secondary)' }}>連携店舗がありません</p>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary mt-4">
            <Plus size={16} className="inline mr-1" />
            最初の店舗を追加
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shops.map((shop) => (
            <div key={shop.id} className="card overflow-hidden">
              {/* ヘッダー */}
              <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: 'var(--border-color)' }}>
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: shop.platform?.color || '#666' }}
                >
                  {shop.platform?.name?.charAt(0) || 'S'}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                    {shop.shopName}
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {shop.platform?.name || shop.platformType}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  shop.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {shop.isActive ? '有効' : '無効'}
                </span>
              </div>

              {/* 統計 */}
              <div className="p-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{shop.orderCount}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>受注数</p>
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{shop.mappingCount}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>商品連携数</p>
                </div>
              </div>

              {/* 同期設定 */}
              <div className="px-4 pb-4">
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <Clock size={14} />
                  最終同期: {shop.lastSyncAt ? new Date(shop.lastSyncAt).toLocaleString('ja-JP') : '未実行'}
                </div>
                {shop.syncSettings && (
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {shop.syncSettings.ordersEnabled && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">受注同期</span>
                    )}
                    {shop.syncSettings.inventoryEnabled && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">在庫同期</span>
                    )}
                    {shop.syncSettings.productsEnabled && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">商品同期</span>
                    )}
                  </div>
                )}
              </div>

              {/* アクション */}
              <div className="p-4 border-t flex items-center gap-2" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--light-gray-primary)' }}>
                <button
                  onClick={() => testConnection(shop.id)}
                  className="flex-1 py-2 text-sm rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                  style={{ color: 'var(--text-primary)' }}
                  title="接続テスト"
                >
                  <Activity size={16} />
                  テスト
                </button>
                <button
                  onClick={() => syncShop(shop.id, 'orders')}
                  className="flex-1 py-2 text-sm rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                  style={{ color: 'var(--text-primary)' }}
                  title="同期実行"
                >
                  <RefreshCw size={16} />
                  同期
                </button>
                <button
                  onClick={() => setViewingShop(shop.id)}
                  className="flex-1 py-2 text-sm rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                  style={{ color: 'var(--text-primary)' }}
                  title="設定"
                >
                  <Settings size={16} />
                  設定
                </button>
                <button
                  onClick={() => deleteShop(shop.id)}
                  className="py-2 px-3 text-sm rounded-lg hover:bg-red-100 transition-colors text-red-600"
                  title="無効化"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 店舗作成モーダル */}
      {showCreateModal && (
        <CreateShopModal
          platforms={platforms}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            setSuccess('店舗を追加しました');
            fetchShops();
          }}
        />
      )}

      {/* 店舗詳細・設定モーダル */}
      {viewingShop && (
        <ShopDetailModal
          shopId={viewingShop}
          onClose={() => setViewingShop(null)}
        />
      )}
    </div>
  );
}

// 店舗作成モーダル
function CreateShopModal({ platforms, onClose, onSuccess }: { platforms: Record<string, Platform>; onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    shopName: '',
    platformType: 'rakuten',
    apiKey: '',
    apiSecret: '',
  });
  const [syncSettings, setSyncSettings] = useState({
    inventoryEnabled: true,
    ordersEnabled: true,
    productsEnabled: false,
    syncFrequency: '15min',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiFetch('/api/shops', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          syncSettings,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" style={{ backgroundColor: 'var(--background-card)' }}>
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>店舗追加</h2>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>店舗名 *</label>
              <input type="text" value={formData.shopName} onChange={(e) => setFormData({ ...formData, shopName: e.target.value })} required placeholder="例: 楽天市場 本店" className="w-full px-4 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>プラットフォーム *</label>
              <select value={formData.platformType} onChange={(e) => setFormData({ ...formData, platformType: e.target.value })} className="w-full px-4 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                {Object.entries(platforms).map(([key, platform]) => (
                  <option key={key} value={key}>{platform.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>APIキー</label>
              <input type="text" value={formData.apiKey} onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })} placeholder="各モールから発行されたAPIキー" className="w-full px-4 py-2 border rounded-lg font-mono text-sm" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>APIシークレット</label>
              <input type="password" value={formData.apiSecret} onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })} placeholder="APIシークレット" className="w-full px-4 py-2 border rounded-lg font-mono text-sm" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>

            {/* 同期設定 */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>同期設定</label>
              <div className="space-y-2 p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={syncSettings.ordersEnabled} onChange={(e) => setSyncSettings({ ...syncSettings, ordersEnabled: e.target.checked })} className="rounded" />
                  <span style={{ color: 'var(--text-primary)' }}>受注同期</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={syncSettings.inventoryEnabled} onChange={(e) => setSyncSettings({ ...syncSettings, inventoryEnabled: e.target.checked })} className="rounded" />
                  <span style={{ color: 'var(--text-primary)' }}>在庫同期</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={syncSettings.productsEnabled} onChange={(e) => setSyncSettings({ ...syncSettings, productsEnabled: e.target.checked })} className="rounded" />
                  <span style={{ color: 'var(--text-primary)' }}>商品同期</span>
                </label>
                <div className="pt-2">
                  <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>同期間隔</label>
                  <select value={syncSettings.syncFrequency} onChange={(e) => setSyncSettings({ ...syncSettings, syncFrequency: e.target.value })} className="ml-2 px-3 py-1 border rounded" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                    <option value="realtime">リアルタイム</option>
                    <option value="5min">5分</option>
                    <option value="15min">15分</option>
                    <option value="30min">30分</option>
                    <option value="1hour">1時間</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary">キャンセル</button>
              <button type="submit" disabled={loading} className="btn-primary">{loading ? '追加中...' : '追加'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// 店舗詳細モーダル
function ShopDetailModal({ shopId, onClose }: { shopId: string; onClose: () => void }) {
  const [shop, setShop] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'edit' | 'logs'>('info');
  const [editForm, setEditForm] = useState({
    shopName: '',
    apiKey: '',
    apiSecret: '',
    isActive: true,
    syncSettings: {
      inventoryEnabled: true,
      ordersEnabled: true,
      productsEnabled: false,
      syncFrequency: '15min',
    },
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchShop();
    fetchLogs();
  }, [shopId]);

  async function fetchShop() {
    try {
      const response = await apiFetch(`/api/shops/${shopId}`);
      const data = await response.json();
      if (response.ok) {
        setShop(data.shop);
        setEditForm({
          shopName: data.shop.shopName || '',
          apiKey: '',
          apiSecret: '',
          isActive: data.shop.isActive ?? true,
          syncSettings: data.shop.syncSettings || {
            inventoryEnabled: true,
            ordersEnabled: true,
            productsEnabled: false,
            syncFrequency: '15min',
          },
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchLogs() {
    try {
      const response = await apiFetch(`/api/shops/${shopId}/logs?limit=20`);
      const data = await response.json();
      if (response.ok) setLogs(Array.isArray(data.logs) ? data.logs : []);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updateData: any = {
        shopName: editForm.shopName,
        isActive: editForm.isActive,
        syncSettings: editForm.syncSettings,
      };
      if (editForm.apiKey) updateData.apiKey = editForm.apiKey;
      if (editForm.apiSecret) updateData.apiSecret = editForm.apiSecret;

      const response = await apiFetch(`/api/shops/${shopId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setSuccess('店舗情報を更新しました');
      fetchShop();
      setActiveTab('info');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" style={{ backgroundColor: 'var(--background-card)' }}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>店舗詳細</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">×</button>
          </div>

          {/* メッセージ */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>
          )}

          {/* タブ */}
          <div className="flex gap-2 mb-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <button
              onClick={() => setActiveTab('info')}
              className={`px-4 py-2 -mb-px ${activeTab === 'info' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : ''}`}
              style={activeTab !== 'info' ? { color: 'var(--text-secondary)' } : {}}
            >
              基本情報
            </button>
            <button
              onClick={() => setActiveTab('edit')}
              className={`px-4 py-2 -mb-px ${activeTab === 'edit' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : ''}`}
              style={activeTab !== 'edit' ? { color: 'var(--text-secondary)' } : {}}
            >
              編集
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`px-4 py-2 -mb-px ${activeTab === 'logs' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : ''}`}
              style={activeTab !== 'logs' ? { color: 'var(--text-secondary)' } : {}}
            >
              同期ログ
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">読み込み中...</div>
          ) : shop ? (
            activeTab === 'info' ? (
              <div className="space-y-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                  <p><strong>店舗名:</strong> {shop.shopName}</p>
                  <p><strong>プラットフォーム:</strong> {shop.platform?.name}</p>
                  <p><strong>ステータス:</strong> {shop.isActive ? '有効' : '無効'}</p>
                  <p><strong>APIキー:</strong> {shop.apiKey || '未設定'}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                  <p className="font-medium mb-2">同期設定</p>
                  <p>受注同期: {shop.syncSettings?.ordersEnabled ? '有効' : '無効'}</p>
                  <p>在庫同期: {shop.syncSettings?.inventoryEnabled ? '有効' : '無効'}</p>
                  <p>商品同期: {shop.syncSettings?.productsEnabled ? '有効' : '無効'}</p>
                  <p>同期間隔: {shop.syncSettings?.syncFrequency}</p>
                </div>
              </div>
            ) : activeTab === 'edit' ? (
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>店舗名</label>
                  <input
                    type="text"
                    value={editForm.shopName}
                    onChange={(e) => setEditForm({ ...editForm, shopName: e.target.value })}
                    required
                    className="w-full px-4 py-2 border rounded-lg"
                    style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>APIキー（変更する場合のみ入力）</label>
                  <input
                    type="text"
                    value={editForm.apiKey}
                    onChange={(e) => setEditForm({ ...editForm, apiKey: e.target.value })}
                    placeholder="変更しない場合は空欄"
                    className="w-full px-4 py-2 border rounded-lg font-mono text-sm"
                    style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>APIシークレット（変更する場合のみ入力）</label>
                  <input
                    type="password"
                    value={editForm.apiSecret}
                    onChange={(e) => setEditForm({ ...editForm, apiSecret: e.target.value })}
                    placeholder="変更しない場合は空欄"
                    className="w-full px-4 py-2 border rounded-lg font-mono text-sm"
                    style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="isActive" style={{ color: 'var(--text-primary)' }}>店舗を有効にする</label>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>同期設定</label>
                  <div className="space-y-2 p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.syncSettings.ordersEnabled}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          syncSettings: { ...editForm.syncSettings, ordersEnabled: e.target.checked }
                        })}
                        className="rounded"
                      />
                      <span style={{ color: 'var(--text-primary)' }}>受注同期</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.syncSettings.inventoryEnabled}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          syncSettings: { ...editForm.syncSettings, inventoryEnabled: e.target.checked }
                        })}
                        className="rounded"
                      />
                      <span style={{ color: 'var(--text-primary)' }}>在庫同期</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.syncSettings.productsEnabled}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          syncSettings: { ...editForm.syncSettings, productsEnabled: e.target.checked }
                        })}
                        className="rounded"
                      />
                      <span style={{ color: 'var(--text-primary)' }}>商品同期</span>
                    </label>
                    <div className="pt-2">
                      <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>同期間隔</label>
                      <select
                        value={editForm.syncSettings.syncFrequency}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          syncSettings: { ...editForm.syncSettings, syncFrequency: e.target.value }
                        })}
                        className="ml-2 px-3 py-1 border rounded"
                        style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                      >
                        <option value="realtime">リアルタイム</option>
                        <option value="5min">5分</option>
                        <option value="15min">15分</option>
                        <option value="30min">30分</option>
                        <option value="1hour">1時間</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button type="button" onClick={() => setActiveTab('info')} className="btn-secondary">キャンセル</button>
                  <button type="submit" disabled={saving} className="btn-primary">{saving ? '保存中...' : '保存'}</button>
                </div>
              </form>
            ) : (
              <div>
                {logs.length === 0 ? (
                  <p className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>同期ログがありません</p>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                        <th className="px-3 py-2 text-left text-xs">日時</th>
                        <th className="px-3 py-2 text-left text-xs">種類</th>
                        <th className="px-3 py-2 text-center text-xs">結果</th>
                        <th className="px-3 py-2 text-right text-xs">処理件数</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id} className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                          <td className="px-3 py-2 text-sm">{new Date(log.createdAt).toLocaleString('ja-JP')}</td>
                          <td className="px-3 py-2 text-sm">{log.syncType}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 text-xs rounded ${log.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {log.status === 'success' ? '成功' : 'エラー'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-right">{log.itemsProcessed ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )
          ) : (
            <div className="text-center py-8">店舗情報を取得できませんでした</div>
          )}

          <div className="flex justify-end mt-6">
            <button onClick={onClose} className="btn-secondary">閉じる</button>
          </div>
        </div>
      </div>
    </div>
  );
}

